import { useEffect } from 'react'
import { fetchToolkitsV1, fetchToolkitV1 } from '../services/api/toolkits'
import { useGlobalStore } from '../services/store/store'
import { toolkitCategoriesV1, toolkitV1 } from '../services/api/endpoints'

const CACHE_NAME = 'api-cache-v29'
const IMAGE_URLS = [
	'/src/assets/icons/toolkit/tool.svg',
	'/src/assets/icons/toolkit/guidance.svg',
	'/src/assets/icons/toolkit/BulletListIcon.png',
	'/src/assets/icons/toolkit/college-of-policing-white-type-logo.png',
	'/src/assets/icons/toolkit/CoP-logo-footer.svg',
	'/src/assets/icons/toolkit/facebook.svg',
	'/src/assets/icons/toolkit/linkedin.svg',
	'/src/assets/icons/toolkit/twitter.svg'
]
const usePreloadToolkits = () => {
	const userToken = useGlobalStore((state) => state.userToken)

	useEffect(() => {
		const preloadToolkits = async () => {
			if (!userToken) return

			try {
				// console.log('[PWA] Preloading all toolkits...')

				const cache = await caches.open(CACHE_NAME)

				const imagePreloadPromises = IMAGE_URLS.map(async (url) => {
					try {
						// console.log(`[PWA] Preloading IMAGE_URLS: ${url}`)
						const response = await fetch(url)

						if (response.ok) {
							await cache.put(url, response)
							// console.log(`[PWA] Cached IMAGE_URLS: ${url}`)
						} else {
							console.warn(
								`[PWA] Failed to fetch IMAGE: ${url}, Status: ${response.status}`
							)
						}
					} catch (error) {
						console.error(`[PWA] Error fetching IMAGE_URLS: ${url}`, error)
					}
				})

				// Wait for IMAGE_URLS to preload
				await Promise.all(imagePreloadPromises)

				// console.log('[PWA] Fetching toolkit categories...')
				const categoriesData = await fetchToolkitsV1()
				// console.log('[PWA] Toolkit categories fetched:', categoriesData)

				await cache.put(
					toolkitCategoriesV1,
					new Response(JSON.stringify(categoriesData))
				)

				const sortedCategories = {
					...(categoriesData.Operational
						? { Operational: categoriesData.Operational }
						: {}),
					...Object.keys(categoriesData)
						.filter((key) => key !== 'Operational')
						.reduce((acc, key) => {
							acc[key] = categoriesData[key]
							return acc
						}, {})
				}

				await Object.values(sortedCategories).reduce(
					async (prevCategory, category) => {
						await prevCategory

						return Object.values(category).reduce(
							async (prevSubCategory, subCategory) => {
								await prevSubCategory

								return Object.values(subCategory.toolkits || {}).reduce(
									async (prevToolkit, toolkit) => {
										await prevToolkit

										const toolkitData = await fetchToolkitV1(toolkit.nid)

										await cache.put(
											`${toolkitV1}/${toolkit.nid}`,
											new Response(JSON.stringify(toolkitData))
										)

										await new Promise((resolve) => {
											setTimeout(resolve, 2000)
										})
									},
									Promise.resolve()
								)
							},
							Promise.resolve()
						)
					},
					Promise.resolve()
				)

				// console.log('[PWA] All toolkits preloaded and cached.')
			} catch (error) {
				console.error('[PWA] Error preloading toolkits:', error)
			}
		}

		preloadToolkits()
		const handleMessage = (event) => {
			if (event.data?.type === 'RELOAD_TOOLKITS') {
				console.log('[PWA] Cache cleared, reloading toolkits...')
				preloadToolkits()
			}
		}
		navigator.serviceWorker.addEventListener('message', handleMessage)

		return () => {
			navigator.serviceWorker.removeEventListener('message', handleMessage)
		}
	}, [userToken])

	useEffect(() => {
		if (!userToken) {
			caches.delete(CACHE_NAME).then((success) => {
				if (success) console.log('[PWA] Cache cleared after logout.')
			})
		}
	}, [userToken])
}

export default usePreloadToolkits
